import angular, { IPromise } from 'angular';
import {
    DjangoLicenseType,
    IDjangoApi1License,
    canUpgrade,
    hasLicenseType,
    hasNonPerpetualLicense,
    hasUpgradeableLicense,
    licenseIsActive
} from '../../tn.common/classes/Model/Licenses';
import DjangoClient from '../../tn.django/django-client.service/djangoClient';

import { EMPTY_FONT_RIGHTS } from '../font-rights';
import { UserService } from '../../tn.user/service.tn.user';


export const HUMAN_READABLE_LICENSE_TYPES = [
    'Desktop',
    'Web',
    'Application',
    'ePub',
    'Broadcast',
    'Server',
    'Game',
    'Embedding',
];
export type HumanReadableLicenseType =
    (typeof HUMAN_READABLE_LICENSE_TYPES)[number];
export const EXPIRATION_STATUSES = ['Active', 'Expired'];
export type ExpirationStatus = (typeof EXPIRATION_STATUSES)[number];

export interface ILicenseFilters {
    name?: string;
    licenseType?: HumanReadableLicenseType[];
    expiration?: ExpirationStatus[];
}

export class LicenseService {
    licenses: IDjangoApi1License[] = [];
    filteredLicenses: IDjangoApi1License[] = [];
    filters: ILicenseFilters = {};
    fontRights = angular.copy(EMPTY_FONT_RIGHTS);
    licensesFromServerPromise?: IPromise<IDjangoApi1License[]>;

    static $inject = ['djangoClient', 'tn.user.service'];
    constructor(
        protected djangoClient: DjangoClient,
        protected userService: UserService,
    ) {
        //refresh licenses on login/logout
        userService.login$.subscribe(() => this.refreshAll());
        userService.logout$.subscribe(() => this.refreshAll());
    }

    getTotalCost(license: IDjangoApi1License): number {
        return Object.keys(license.fontRights).reduce((total, rightName) => {
            return total + parseFloat(license.fontRights[rightName].price);
        }, 0);
    }

    sortByName(licenseA: IDjangoApi1License, licenseB: IDjangoApi1License): 0 | 1 | -1 {
        const nameA = licenseA.style.family_name.toUpperCase();
        const nameB = licenseB.style.family_name.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    }

    get(): IDjangoApi1License[] {
        return this.getLicenses();
    }
    getLicenses(): IDjangoApi1License[] {
        return this.licenses;
    }

    getFilteredLicenses(): IDjangoApi1License[] {
        return this.filteredLicenses;
    }

    getLicensesFromServer(): IPromise<IDjangoApi1License[]> {
        // Wait until we need to before we actually fetch
        if (this.licensesFromServerPromise) {
            return this.licensesFromServerPromise;
        } else {
            return this.refreshAll();
        }
    }

    refreshAll(): IPromise<IDjangoApi1License[]> {
        this.licensesFromServerPromise =
            this.djangoClient.get<IDjangoApi1License[]>('/api/1/licenses/');
        return this.licensesFromServerPromise;
    }

    setFilteredLicenses(
        filters: ILicenseFilters,
        newLicenses?: IDjangoApi1License[]
    ): void {
        if (newLicenses) {
            this.licenses.splice(0, this.licenses.length);
            this.licenses.push(...newLicenses.slice());
        }

        let filteredLicenses = this.licenses.slice();
        const name = filters.name;
        if (name) {
            filteredLicenses = filteredLicenses.filter((license) =>
                this._styleNameFilter(license, name)
            );
        }
        const licenseType = filters.licenseType;
        if (licenseType && licenseType.length !== 7) {
            filteredLicenses = filteredLicenses.filter((license) =>
                this._licenseTypeFilter(license, licenseType)
            );
        }
        const expiration = filters.expiration;
        if (expiration && expiration.length !== 2) {
            filteredLicenses = filteredLicenses.filter((license) =>
                this._expirationFilter(license, expiration)
            );
        }

        this.filteredLicenses.splice(0, this.filteredLicenses.length);
        this.filteredLicenses.push(...filteredLicenses);
    }

    hasNonPerpetualLicense(licenses: IDjangoApi1License[]): boolean {
        return hasNonPerpetualLicense(licenses);
    }

    hasLimitedLicense(licenses: IDjangoApi1License[]): boolean {
        return this.hasLimitedLicense(licenses);
    }

    hasUpgradeableLicense(licenses: IDjangoApi1License[]): boolean {
        return hasUpgradeableLicense(licenses);
    }

    canUpgrade(license: IDjangoApi1License, type: DjangoLicenseType): boolean {
        return canUpgrade(license, type);
    }

    protected _licenseTypeFilter(
        license: IDjangoApi1License,
        licenseType: HumanReadableLicenseType[]
    ): IDjangoApi1License | undefined {
        if (
            licenseType.indexOf('Desktop') > -1 &&
            hasLicenseType(license, 'desktop')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Web') > -1 &&
            hasLicenseType(license, 'web')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Application') > -1 &&
            hasLicenseType(license, 'app')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('ePub') > -1 &&
            hasLicenseType(license, 'epub')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Broadcast') > -1 &&
            hasLicenseType(license, 'broadcast')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Server') > -1 &&
            hasLicenseType(license, 'server')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Game') > -1 &&
            hasLicenseType(license, 'game')
        ) {
            return license;
        }
        if (
            licenseType.indexOf('Embedding') > -1 &&
            hasLicenseType(license, 'embedding')
        ) {
            return license;
        }
    }

    protected _expirationFilter(
        license: IDjangoApi1License,
        expiration: ExpirationStatus[]
    ): IDjangoApi1License | undefined {
        if (
            expiration.indexOf('Active') > -1 &&
            licenseIsActive(license)
        ) {
            return license;
        }
        if (
            expiration.indexOf('Expired') > -1 &&
            !licenseIsActive(license)
        ) {
            return license;
        }
    }

    protected _styleNameFilter(
        license: IDjangoApi1License,
        name: string
    ): boolean {
        return (
            license.style.name.toLowerCase().indexOf(name.toLowerCase()) >= 0
        );
    }

    set(licenses: IDjangoApi1License[]): void {
        this.setLicenses(licenses);
    }
    setLicenses(licenses: IDjangoApi1License[]): void {
        this.licenses.splice(0, this.licenses.length);
        this.licenses.push(...licenses);
    }
}
